import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaApple,
  FaGooglePlay,
  FaHeart,
  FaInstagram,
  FaTwitter,
  FaFacebook,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import Logo from "./Logo";

const Footer = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const socialLinks = [
    { icon: <FaInstagram />, href: "#", label: "Instagram" },
    { icon: <FaTwitter />, href: "#", label: "Twitter" },
    { icon: <FaFacebook />, href: "#", label: "Facebook" },
  ];

  const footerLinks = [
    {
      title: "Legal",
      links: [
        "Terms of Use",
        "Privacy Policy",
        "Child Safety",
        "Cookie Policy",
      ],
    },
    { title: "Support", links: ["FAQ's", "Safety Tips"] },
  ];

  return (
    <footer className="relative bg-black bg-opacity-90 backdrop-blur-lg py-16 overflow-hidden border-t border-white border-opacity-10">
      {/* Subtle overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-50" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
            {/* Brand Section */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <div className="text-center">
                <img
                  src="/assets/moon-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0"
                />
                {/* </div>
          <div className='text-center lg:text-left'> */}
                <img
                  src="/assets/png-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0 pb-2"
                />
              </div>
              {/* <Logo size="large" className="mb-4 justify-start" /> */}
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                The video dating revolution starts in Bournemouth. Join singles
                every night at 7pm for meaningful connections that lead to
                real-life dates.
              </p>

              {/* Location */}
              <div className="flex items-center gap-2 mb-6">
                <FaMapMarkerAlt className="text-romantic-pink" />
                <span className="text-gray-300">
                  Launching in Bournemouth, UK
                </span>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://apps.apple.com/gb/app/datenite-app/id1592597688"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <motion.button
                    className="glow-button bg-gradient-to-r from-romantic-purple to-romantic-pink text-white px-6 py-3 rounded-full font-semibold flex items-center gap-3 justify-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaApple className="text-xl" />
                    <div className="text-left">
                      <div className="text-xs opacity-75">Download on the</div>
                      <div className="text-sm font-bold">App Store</div>
                    </div>
                  </motion.button>
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=uk.co.datenite.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <motion.button
                    className="glow-button bg-gradient-to-r from-romantic-blue to-romantic-purple text-white px-6 py-3 rounded-full font-semibold flex items-center gap-3 justify-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaGooglePlay className="text-xl" />
                    <div className="text-left">
                      <div className="text-xs opacity-75">Download on the</div>
                      <div className="text-sm font-bold">Google Play</div>
                    </div>
                  </motion.button>
                </a>
              </div>
            </motion.div>

            {/* Links Sections */}
            {footerLinks.map((section, index) => (
              <motion.div key={section.title} variants={itemVariants}>
                <h3 className="text-white font-bold text-lg mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => {
                    const getPath = (linkName) => {
                      switch (linkName) {
                        case "Terms of Use":
                          return "/terms";
                        case "Privacy Policy":
                          return "/privacy";
                        case "Child Safety":
                          return "/child-safety";
                        case "Safety Tips":
                          return "/safety-tips";
                        case "FAQ's":
                          return "/faq";
                        case "Cookie Policy":
                          return "/cookie-policy";
                        default:
                          return "#";
                      }
                    };

                    const isRoutableLink = [
                      "Terms of Use",
                      "Privacy Policy",
                      "Child Safety",
                      "Safety Tips",
                      "FAQ's",
                      "Cookie Policy"
                    ].includes(link);

                    return (
                      <li key={link}>
                        {isRoutableLink ? (
                          <motion.div whileHover={{ x: 5 }}>
                            <Link
                              to={getPath(link)}
                              className="text-gray-300 hover:text-romantic-pink transition-colors duration-300 text-left block"
                            >
                              {link}
                            </Link>
                          </motion.div>
                        ) : (
                          <motion.button
                            className="text-gray-300 hover:text-romantic-pink transition-colors duration-300 text-left"
                            whileHover={{ x: 5 }}
                          >
                            {link}
                          </motion.button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Divider */}
          <motion.div
            variants={itemVariants}
            className="border-t border-gray-800 pt-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              {/* Copyright */}
              <div className="flex items-center gap-2 text-gray-400">
                <span>© 2025 datenite. Made with</span>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <FaHeart className="text-romantic-pink" />
                </motion.div>
                <span>in Bournemouth</span>
              </div>

              {/* Social Links */}
              {/* <div className="flex items-center gap-4">
                <span className="text-gray-400 mr-2">Follow us:</span>
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    className="w-10 h-10 bg-gradient-to-br from-romantic-purple to-romantic-pink rounded-full flex items-center justify-center text-white"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div> */}
            </div>
          </motion.div>

          {/* Newsletter Signup */}
          <motion.div variants={itemVariants} className="mt-12 text-center">
            <motion.div
              className="glass rounded-2xl p-8 max-w-2xl mx-auto relative overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple to-romantic-pink opacity-10" />
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Get in Touch With Us
                </h3>
                
                <div className="flex flex-col items-center gap-2 max-w-md mx-auto">
                  <p className="text-gray-300 text-center mb-2">
                    Have questions? Reach out to us at:
                  </p>
                  <motion.a 
                    href="mailto:support@datenite.co.uk" 
                    className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors text-lg font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaEnvelope className="text-xl" />
                    support@datenite.co.uk
                  </motion.a>
                  <p className="text-gray-400 text-sm mt-2 text-center">
                    We typically respond within 24 hours
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating Hearts */}
      <motion.div
        className="absolute bottom-10 left-10 text-romantic-pink text-2xl"
        animate={{
          y: [0, -20, 0],
          rotate: [0, 10, -10, 0],
        }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <FaHeart />
      </motion.div>

      <motion.div
        className="absolute top-10 right-10 text-romantic-blue text-xl"
        animate={{
          y: [0, 15, 0],
          rotate: [0, -10, 10, 0],
        }}
        transition={{ duration: 5, repeat: Infinity, delay: 2 }}
      >
        <FaHeart />
      </motion.div>
    </footer>
  );
};

export default Footer;
